import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/ApiProvider';
import type {
  CreateTemplateInput,
  CreateTemplateVersionInput,
  DocumentListParams,
  GenerateDocumentInput,
  GenerateScenarioInput,
} from '../../lib/api';

/**
 * Query keys for the LOT 7A documents. Dossier-scoped reads live under
 * `['documents', dossierId]` so a generation mutation can invalidate the dossier's document
 * list + generation runs in one shot. Templates are a separate, global namespace.
 */
export const documentKeys = {
  all: ['documents'] as const,
  dossier: (dossierId: string) => ['documents', dossierId] as const,
  list: (dossierId: string, params: DocumentListParams) =>
    ['documents', dossierId, 'list', params] as const,
  runs: (dossierId: string) => ['documents', dossierId, 'runs'] as const,
  detail: (id: string) => ['documents', 'detail', id] as const,
  manifest: (id: string) => ['documents', 'manifest', id] as const,
  pipeline: (id: string) => ['documents', 'pipeline', id] as const,
};

export const templateKeys = {
  all: ['document-templates'] as const,
  list: () => ['document-templates', 'list'] as const,
  detail: (id: string) => ['document-templates', 'detail', id] as const,
};

// --- Dossier-scoped documents --------------------------------------------------

/** List generated documents of a dossier (history section). */
export function useDocuments(dossierId: string, params: DocumentListParams = {}, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: documentKeys.list(dossierId, params),
    queryFn: () => api.listDocuments(dossierId, params),
    enabled,
  });
}

/** List document generation attempts (SUCCESS / BLOCKED audit trail). */
export function useGenerationRuns(dossierId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: documentKeys.runs(dossierId),
    queryFn: () => api.listGenerationRuns(dossierId),
    enabled,
  });
}

/** Read a single document manifest (read-only; server-computed hash). */
export function useDocumentManifest(id: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: documentKeys.manifest(id ?? ''),
    queryFn: () => api.getDocumentManifest(id ?? ''),
    enabled: enabled && id !== null && id !== '',
  });
}

/** Generate a DOCX: submit intent; the server builds the manifest & computes the official hash. */
export function useGenerateDocument(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateDocumentInput) => api.generateDocument(dossierId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentKeys.dossier(dossierId) }),
  });
}

/**
 * Generate the whole document set for the dossier's scenario (LOT 8B/8C). The SERVER derives which
 * documents to produce from `dossier.scenario` and enforces PV/Rapport coherence + inheritance; the
 * web only submits the validated calc_run and refreshes the dossier's documents on success.
 */
export function useGenerateScenario(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GenerateScenarioInput) => api.generateScenario(dossierId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentKeys.dossier(dossierId) }),
  });
}

/**
 * LOT 9A — the PV variants the SERVER offers for this case, with prerequisites, readiness and the
 * reason a variant is blocked. The Web renders this payload and derives nothing from it.
 */
export function usePvVariants(dossierId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: [...documentKeys.dossier(dossierId), 'pv-variants'],
    queryFn: () => api.listPvVariants(dossierId),
    enabled: enabled && dossierId !== '',
  });
}

/** Finalize a document — blocked in LOT 7A until pdfaStatus VALID (409 surfaced). */
export function useFinalizeDocument(dossierId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.finalizeDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: documentKeys.dossier(dossierId) }),
  });
}

// --- LOT 7B : archival pipeline (PDF/A) ----------------------------------------

/** Read the authoritative pipeline state for a document (server converts/validates, never the web). */
export function usePipelineStatus(id: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: documentKeys.pipeline(id ?? ''),
    queryFn: () => api.getPipelineStatus(id ?? ''),
    enabled: enabled && id !== null && id !== '',
  });
}

/** Start the server-side archival pipeline; refresh the pipeline state + dossier document list. */
export function useStartPipeline(dossierId: string, documentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.startPipeline(documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.pipeline(documentId) });
      void qc.invalidateQueries({ queryKey: documentKeys.dossier(dossierId) });
    },
  });
}

/** Relaunch the pipeline after a failed run. */
export function useRetryPipeline(dossierId: string, documentId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.retryPipeline(documentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: documentKeys.pipeline(documentId) });
      void qc.invalidateQueries({ queryKey: documentKeys.dossier(dossierId) });
    },
  });
}

// --- Templates (global) --------------------------------------------------------

export function useTemplates(enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: () => api.listTemplates(),
    enabled,
  });
}

export function useCreateTemplate() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateInput) => api.createTemplate(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useCreateTemplateVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateTemplateVersionInput }) =>
      api.createTemplateVersion(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}

export function useActivateTemplateVersion() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) => api.activateTemplateVersion(versionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: templateKeys.all }),
  });
}
